import React from "react"
import { cond, T } from "ramda"
import { useQuery } from "@apollo/client"
import { withAuthProvider } from "../../../lib/auth"
import QuickLinks from "../QuickLinks"
import { Side } from "../Panel"
import { NewMSRCreditsBrief, NewCompactPDRBrief } from "../PDRCredits"
import {
  isMember,
  isAssociate,
  isAffiliate,
  isPendingAssociate,
  isAffiliateToAssociatePending,
  isStudent,
  hasReciprocityAttribute,
  hasStartedReciprocityLevels,
  isB25Class,
  hasProspectDashboard,
  isProspectPath,
} from "../../utils"
import CourseCurriculum, { Loading } from "../CourseCurriculum/CourseCurriculum"
import PathBox from "../Prospect/PathBox"
import { withProspectDateQuery } from "../../../utils/prospect/withProspectDateQuery"
import { useMembershipJourney } from "../../../hooks/useMembershipJourney"
import { usePgmGraduate } from "../../../hooks/useMemberApplicationEligibility"
import { shouldRoutePendingAffiliate } from "../../utils"
import { MembershipJourneyProvider } from "../MembershipJourney/MembershipJourneyContext"
import { MEMBER_APPLICATION_ELIGIBILITY_QUERY } from "../../queries/memberApplicationEligibility"
import { COACH_TOOLS_API } from "../../../apolloClient"
import { shouldShowCourseCurriculumAdditionalRequirements } from "../MembershipJourney/utils/shouldShowCourseCurriculumAdditionalRequirements"

export const CourseCurriculumBriefing = ({ collapsed, person }) => {
  const { pgmGraduate } = usePgmGraduate({
    skip: !person || !isStudent(person),
  })
  // Eligibility only feeds the Additional Requirements CTA, so skip the read
  // whenever the CTA cannot show: collapsed briefing, qualifying associates,
  // qualifying students (mid-program past QL and graduates load it), and
  // registered Affiliates (Member-apply checklist does not apply).
  const skipEligibilityQuery =
    !person ||
    collapsed ||
    !shouldShowCourseCurriculumAdditionalRequirements({
      me: person,
      pgmGraduate,
    })
  const { data: eligibilityData, loading: eligibilityQueryLoading } = useQuery(
    MEMBER_APPLICATION_ELIGIBILITY_QUERY,
    {
      context: { api: COACH_TOOLS_API },
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
      skip: skipEligibilityQuery,
    },
  )

  if (collapsed) {
    return <div />
  }

  return (
    <MembershipJourneyProvider
      value={{
        me: person,
        pgaId: person?.pgaId,
        pgmProgress: person?.pgmProgress,
        pgmGraduate,
        memberApplicationEligibility:
          eligibilityData?.memberApplicationEligibility,
        // cache-and-network sets loading=true on background refetches; only
        // report the initial load so consumers don't flicker on refetch.
        memberApplicationEligibilityLoading:
          eligibilityQueryLoading && !eligibilityData,
      }}
    >
      <CourseCurriculum />
    </MembershipJourneyProvider>
  )
}

export const PdrWidget = ({ collapsed }) => {
  return collapsed ? <NewCompactPDRBrief /> : <NewMSRCreditsBrief />
}

// Ramda cond invokes handlers as plain functions; hook-using views must render
// as JSX elements so React owns hook state in a child fiber.
const renderCourseCurriculumBriefing = props => (
  <CourseCurriculumBriefing {...props} />
)

export const chooseAppropriateViewForUser = cond([
  [({ loading }) => loading, () => <Loading />],
  [({ routingMe }) => isMember(routingMe), props => <PdrWidget {...props} />],
  [({ routingMe }) => isB25Class(routingMe), props => <PdrWidget {...props} />],
  [({ routingMe }) => isAssociate(routingMe), renderCourseCurriculumBriefing],
  // Registered Affiliates may have mainProgramTypeCode AF (Coach Tools), which
  // isAssociate does not match — keep Course Curriculum for that shape.
  [({ routingMe }) => isAffiliate(routingMe), renderCourseCurriculumBriefing],
  [
    ({ routingMe }) => isAffiliateToAssociatePending(routingMe),
    renderCourseCurriculumBriefing,
  ],
  [
    ({ routingMe }) => isPendingAssociate(routingMe),
    ({ collapsed }) => <QuickLinks collapsed={collapsed} />,
  ],
  [({ routingMe }) => isStudent(routingMe), renderCourseCurriculumBriefing],
  // Only show Reciprocity if they have started Z-levels, otherwise let prospects see PathBox
  [
    ({ routingMe }) =>
      hasReciprocityAttribute(routingMe) &&
      hasStartedReciprocityLevels(routingMe),
    renderCourseCurriculumBriefing,
  ],
  [
    ({ routingMe, currentMember }) =>
      hasProspectDashboard(routingMe) ||
      shouldRoutePendingAffiliate(routingMe) ||
      isProspectPath(currentMember),
    ({ collapsed }) => (collapsed ? <div /> : <PathBox />),
  ],
  [T, ({ collapsed }) => <QuickLinks collapsed={collapsed} />],
])

export const MembershipSailingView = ({
  me,
  authReady,
  collapsed,
  currentMember,
}) => {
  const { person, loading: personLoading } = useMembershipJourney()
  const routingMe = person || me

  return (
    <Side basis={!collapsed && 55}>
      {chooseAppropriateViewForUser({
        me,
        person: routingMe,
        routingMe,
        loading: !authReady || (personLoading && !person),
        collapsed,
        currentMember,
      })}
    </Side>
  )
}

export default withProspectDateQuery(withAuthProvider(MembershipSailingView))
